import * as S from "../styles";
import * as yup from "yup";

/* eslint-disable @typescript-eslint/no-unused-vars */
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid } from "@mui/material";
import { useEffect, useState } from "react";

import AutoComplete from "@/components/hookForms/autoComplete";
import { Button } from "@mui/material";
import CheckBox from "@/components/hookForms/checkbox";
import CloseIcon from "@mui/icons-material/Close";
import DatePickerMui from "@/components/hookForms/datePicker";
import IGenericResult from "@/interfaces/IGenericResult";
import IconButton from "@mui/material/IconButton";
import RadioButton from "@/components/hookForms/radioButton";
import { format } from "date-fns";
import messages from "@/utils/messages";
import serviceClientes from "@/services/serviceCliente";
import serviceInstalacao from "@/services/serviceInstalacao";
import serviceParceiro from "@/services/serviceParceiro";
import serviceRelatorio from "@/services/serviceRelatorio";
import serviceUsina from "@/services/serviceUsina";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

export interface IDialogProps {
    open: boolean;
    onClose?: any;
}

export default function BalancoCredito({ open, onClose }: IDialogProps) {
    const [usinaDataOptions, setUsinaDataOptions] = useState<IGenericResult[]>([]);
    const [instalacaoDataOptions, setInstalacaoDataOptions] = useState<IGenericResult[]>([]);
    const [clienteOptions, setClienteOptions] = useState<IGenericResult[]>([]);
    const [parceirosDataOptions, setParceirosDataOptions] = useState<IGenericResult[]>([]);
    const [isDisable, setDisable] = useState(false);

    const validationSchema = yup.object().shape({
        instalacaoID: yup.string().required(messages.requiredField),
        clienteID: yup.string().required(messages.requiredField),
        concessionariaID: yup.string().required(messages.requiredField),
        inicio: yup.string().required(messages.requiredField).typeError(messages.requiredField),
        fim: yup.string().required(messages.requiredField).typeError(messages.requiredField),
    });

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(validationSchema),
        defaultValues: {
            inicio: "",
            fim: "",
            instalacaoID: "",
            clienteID: "",
            concessionariaID: "",
        },
    });

    useEffect(() => {
        setUsinaData();
        setInstalacaoData();
        setClienteData();
        setParceirosData();
    }, []);

    const setParceirosData = async () => {
        serviceParceiro
            .getParceiros()
            .then((result) => {
                setParceirosDataOptions(result?.data);
            })
            .catch(() => {
                setParceirosDataOptions([]);
            });
    };

    const setUsinaData = async () => {
        serviceUsina
            .getUsinas()
            .then((result) => {
                setUsinaDataOptions(result?.data);
            })
            .catch(() => {
                setUsinaDataOptions([]);
            });
    };

    const setInstalacaoData = async () => {
        serviceInstalacao
            .getInstalacoes()
            .then((result) => {
                setInstalacaoDataOptions(result?.data);
            })
            .catch(() => {
                setInstalacaoDataOptions([]);
            });
    };

    const setClienteData = async () => {
        serviceClientes
            .getClientes()
            .then((result) => {
                setClienteOptions(result?.data);
            })
            .catch(() => {
                setClienteOptions([]);
            });
    };

    const onSubmit = (values: any) => {
        setDisable(true);

        let inicio = new Date(values.inicio);
        let fim = new Date(values.fim);

        const newValues = {
            ...values,
            inicio: values.inicio ? format(inicio, "yyyy-MM-01") : "",
            fim: values.fim ? format(fim, "yyyy-MM-01") : "",
        };

        serviceRelatorio
            .getBalancoCredito(newValues)
            .then((result) => {
                if (result) {
                    toast.success(messages.reportSucess);
                }
            })
            .catch(() => {
                toast.error(messages.reportError);
            })
            .finally(() => {
                setDisable(false);
            });
    };

    return (
        <div>
            <Dialog
                maxWidth="md"
                fullWidth
                onClose={(_, reason) => {
                    if (reason !== "backdropClick") {
                        onClose();
                    }
                }}
                open={open}
            >
                <DialogTitle>
                    <S.Title>Gerar relatório: Faturas publicadas</S.Title>
                    <IconButton
                        aria-label="close"
                        onClick={() => {
                            reset();
                            onClose(onclose);
                        }}
                        sx={{
                            position: "absolute",
                            right: 8,
                            top: 8,
                            // color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <DialogContent dividers>
                        <Grid item xs={12}>
                            {isDisable && (
                                <S.Loader>
                                    <img alt="Loading" src="/Loading.gif" width="40px" height="40px" />
                                </S.Loader>
                            )}
                        </Grid>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <DatePickerMui
                                    name="inicio"
                                    label="Inicio"
                                    format="MM/yyyy"
                                    view={["month", "year"]}
                                    control={control}
                                    error={errors}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <DatePickerMui
                                    name="fim"
                                    label="Fim"
                                    format="MM/yyyy"
                                    view={["month", "year"]}
                                    control={control}
                                    error={errors}
                                />
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid item xs={12} md={12}>
                                <DatePickerMui
                                    name="ciclo"
                                    label="Ciclos de faturamento"
                                    format="MM/yyyy"
                                    view={["month", "year"]}
                                    control={control}
                                    error={errors}
                                />
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <AutoComplete
                                    name="clienteID"
                                    label="Cliente"
                                    control={control}
                                    error={errors}
                                    options={clienteOptions}
                                    concatWithID={true}
                                />
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <AutoComplete
                                    name="usinaID"
                                    label="Usinas"
                                    options={usinaDataOptions}
                                    control={control}
                                    error={errors}
                                />
                            </Grid>
                            <Grid item xs={12} md={12}>
                                <CheckBox
                                    name="redirecionar"
                                    label="Redirecionar para a tela de relatório"
                                    control={control}
                                    error={errors}
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions
                        style={{ display: "flex", justifyContent: "center", marginTop: 20, marginBottom: 20 }}
                    >
                        <Button
                            style={{ width: "159px", height: "44px" }}
                            onClick={() => {
                                reset();
                                onClose(onClose);
                            }}
                            color="warning"
                            variant="contained"
                        >
                            Cancelar
                        </Button>
                        <Button
                            disabled={isDisable}
                            style={{ width: "159px", height: "44px" }}
                            onClick={() => {}}
                            type="submit"
                            variant="contained"
                        >
                            Gerar
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </div>
    );
}
